import React, { useCallback, useEffect, useMemo, useState } from "react";
import DetailsHeader from "../DetailsHeader/DetailsHeader";
import Styles from "./Connection.module.css";
import { ReactComponent as Chat } from "../../../assets/images/Chat_icon.svg";
import { ReactComponent as ChatActive } from "../../../assets/images/Chat_active.svg";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import ConnectionModal from "../../Modals/ConGen/Connection/ConnectionModal";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import ChatApplication from "../../ChatApplication/ChatApplication";
import Skeleton from "@mui/material/Skeleton";
import emptySelfListing from "../../../assets/images/emptyConnection.png";
import { fetchSelfCapacityById } from "../../../Redux/Slices/CapacitySlice";
import { openBilateralChat } from "../../Modals/Banking/CreateEdit/helpers";
import { getDamData } from "../../../Services/BuySell/Listing";

function EmptyState() {
  return (
    <div className={Styles.emptyState}>
      <img
        style={{ marginBottom: "10px" }}
        src={emptySelfListing}
        alt="No Available Listings"
      />
      <h2>No Counterparty Connections </h2>

      <p>
        You haven’t sent or received any connection requests. We’ll notify you
        as soon as one becomes available.
      </p>
    </div>
  );
}

const StatusPill = ({ status }) => {
  const normalized = String(status || "").toUpperCase();
  const clsMap = {
    ACCEPTED: Styles.statusAccepted,
    REJECTED: Styles.statusRejected,
    PLACED: Styles.statusPlaced,
    RECEIVED: Styles.statusReceived,
  };
  const cls = clsMap[normalized] || Styles.statusDefault;

  return (
    <span className={`${Styles.statusPill} ${cls}`}>
      {normalized.toLowerCase().replace(/_/g, " ")}
    </span>
  );
};

const ROWS_TO_SHOW = 8; // Number of skeleton rows to display

const Connection = () => {
  const location = useLocation();
  const listingId = useMemo(
    () => new URLSearchParams(location.search).get("id"),
    [location.search]
  );

  const [connectionDetails, setConnectionDetails] = useState(false);

  const [chatOpen, setChatOpen] = useState(false);
  const [chatId, setChatId] = useState("");
  const [chatName, setChatName] = useState("");
  const [selfReq, setSelfReq] = useState(null);
  const [damData, setDamData] = useState({});

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const user = useSelector(
    (state) => state.persistedReducer.user?.activeRole
  )?.toLowerCase();

  const dispatch = useDispatch();
  const activeMain = useSelector((state) => state?.CapacitySlice);

  const data = useSelector((state) => state?.CapacitySlice?.ledgerBids);

  const self = useSelector((store) => store?.CapacitySlice?.getSelfListingById);

  useEffect(() => {
    handleDamData();
  }, []);

  useEffect(() => {
    dispatch(fetchSelfCapacityById({ user, id: listingId }));
  }, [dispatch, listingId, user]);

  const handleOpenConnection = useCallback((row) => {
    setConnectionDetails(row);
  }, []);

  const handleConnectionClose = useCallback(() => {
    setConnectionDetails(false);
  }, []);

  const handleChatOpen = (id, name) => {
    setChatId(id);
    setChatName(name);
    setChatOpen(true);
  };

  const handleDamData = async () => {
    let res = await getDamData();
    console.debug(res, "res");
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  useEffect(() => {
    if (self) {
      setSelfReq(self);
    }
  }, [self]);

  const sortedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const getViewElem = (ele) =>
        (user === "consumer" ? ele?.generator : ele?.consumer) || {};
      const viewA = getViewElem(a);
      const viewB = getViewElem(b);

      const slotsA = viewA?.slots || [];
      const slotsB = viewB?.slots || [];

      const getValue = (viewElem, key) => {
        switch (key) {
          case "deliveryType":
            if (viewElem?.isInterstate === true) return "Interstate";
            if (viewElem?.isInterstate === false) return "Intrastate";
            return "";
          case "maxQty":
            return slotsA.length
              ? Math.max(
                  ...slotsA.map((s) => s.sellEnergy ?? s.purchaseEnergy ?? 0)
                )
              : 0;
          case "minQty":
            return slotsA.length
              ? Math.min(
                  ...slotsA.map((s) => s.sellEnergy ?? s.purchaseEnergy ?? 0)
                )
              : 0;
          case "maxRate":
            return slotsA.length
              ? Math.max(
                  ...slotsA.map((s) => s.sellingCost ?? s.landingCost ?? 0)
                )
              : 0;
          case "minRate":
            return slotsA.length
              ? Math.min(
                  ...slotsA.map((s) => s.sellingCost ?? s.landingCost ?? 0)
                )
              : 0;
          case "status":
            return String(viewElem?.status || "");
          default:
            return "";
        }
      };

      let valA = getValue(viewA, sortConfig.key);
      let valB = getValue(viewB, sortConfig.key);

      if (typeof valA === "string") {
        valA = valA.toLowerCase();
        valB = valB.toLowerCase();
      }

      if (sortConfig.direction === "asc") {
        return valA > valB ? 1 : valA < valB ? -1 : 0;
      } else {
        return valA < valB ? 1 : valA > valB ? -1 : 0;
      }
    });
  }, [data, user, sortConfig]);

  // Add a loading state if you have one, or infer from data
  const isLoading = useSelector(
    (state) => state?.CapacitySlice?.fetchLedgerBidsLoading
  );

  const activeListingStatus = selfReq?.status;

  return (
    <>
      {connectionDetails && (
        <ConnectionModal
          currentListing={self}
          open={connectionDetails}
          wholeData={data}
          handleClose={handleConnectionClose}
          connectionText="Connection Request"
        />
      )}

      <div className={Styles.connTopMain}>
        <div className="connectionNav">
          <DetailsHeader
            status={activeListingStatus}
            heading={self?.name}
            displayActive="Connections"
            dispalyList={[
              {
                name: "Connections",
                url: listingId ? `/ledger?id=${listingId}` : "/ledger",
              },
              {
                name: "Active Connection",
                url: listingId ? `/bilateral?id=${listingId}` : "/bilateral",
              },
              {
                name: "Ongoing Connection",
                url: listingId ? `/ongoing?id=${listingId}` : "/ongoing",
              },
            ]}
          />
        </div>

        <div className="connectionContentMain">
          <div className="display_flex_row tableHeading">
            <div style={{ width: "5%", fontWeight: 500, textAlign: "left" }}>
              S.No
            </div>
            <div style={{ width: "15%", fontWeight: 500 }}>Listing Id</div>
            <div
              style={{
                width: "10%",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0,
              }}
              onClick={() => handleSort("deliveryType")}
            >
              Delivery Type{" "}
              {/* {sortConfig.key === "deliveryType" ? (
                sortConfig.direction === "asc" ? (
                  <StraightIcon
                    style={{ transform: "rotate(-180deg)", fontSize: 16 }}
                  />
                ) : (
                  <StraightIcon style={{ fontSize: 16 }} />
                )
              ) : (
                <>
                  <StraightIcon
                    style={{
                      transform: "rotate(-180deg)",
                      fontSize: 16,
                      opacity: 0.5,
                    }}
                  />
                  <StraightIcon
                    style={{
                      fontSize: 16,
                      opacity: 0.5,
                    }}
                  />
                </>
              )} */}
            </div>
            <div
              style={{
                width: "15%",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
              onClick={() => handleSort("maxQty")}
            >
              Max Qty{" "}
              {/* {sortConfig.key === "maxQty" ? (
                sortConfig.direction === "asc" ? (
                  <StraightIcon
                    style={{ transform: "rotate(-180deg)", fontSize: 16 }}
                  />
                ) : (
                  <StraightIcon style={{ fontSize: 16 }} />
                )
              ) : (
                <>
                  <StraightIcon
                    style={{
                      transform: "rotate(-180deg)",
                      fontSize: 16,
                      opacity: 0.5,
                    }}
                  />
                  <StraightIcon
                    style={{
                      fontSize: 16,
                      opacity: 0.5,
                    }}
                  />
                </>
              )} */}
            </div>
            <div
              style={{
                width: "15%",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
              onClick={() => handleSort("minQty")}
            >
              Min Qty{" "}
              {/* {sortConfig.key === "minQty" ? (
                sortConfig.direction === "asc" ? (
                  <StraightIcon
                    style={{ transform: "rotate(-180deg)", fontSize: 16 }}
                  />
                ) : (
                  <StraightIcon style={{ fontSize: 16 }} />
                )
              ) : (
                <>
                  <StraightIcon
                    style={{
                      transform: "rotate(-180deg)",
                      fontSize: 16,
                      opacity: 0.5,
                    }}
                  />
                  <StraightIcon
                    style={{
                      fontSize: 16,
                      opacity: 0.5,
                    }}
                  />
                </>
              )} */}
            </div>
            <div
              style={{
                width: "15%",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
              onClick={() => handleSort("maxRate")}
            >
              Max Rate{" "}
              {/* {sortConfig.key === "maxRate" ? (
                sortConfig.direction === "asc" ? (
                  <StraightIcon
                    style={{ transform: "rotate(-180deg)", fontSize: 16 }}
                  />
                ) : (
                  <StraightIcon style={{ fontSize: 16 }} />
                )
              ) : (
                <>
                  <StraightIcon
                    style={{
                      transform: "rotate(-180deg)",
                      fontSize: 16,
                      opacity: 0.5,
                    }}
                  />
                  <StraightIcon
                    style={{
                      fontSize: 16,
                      opacity: 0.5,
                    }}
                  />
                </>
              )} */}
            </div>
            <div
              style={{
                width: "15%",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
              onClick={() => handleSort("minRate")}
            >
              Min Rate{" "}
              {/* {sortConfig.key === "minRate" ? (
                sortConfig.direction === "asc" ? (
                  <StraightIcon
                    style={{ transform: "rotate(-180deg)", fontSize: 16 }}
                  />
                ) : (
                  <StraightIcon style={{ fontSize: 16 }} />
                )
              ) : (
                <>
                  <StraightIcon
                    style={{
                      transform: "rotate(-180deg)",
                      fontSize: 16,
                      opacity: 0.5,
                    }}
                  />
                  <StraightIcon
                    style={{
                      fontSize: 16,
                      opacity: 0.5,
                    }}
                  />
                </>
              )} */}
            </div>
            <div
              style={{
                width: "10%",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
              onClick={() => handleSort("status")}
            >
              Status{" "}
              {/* {sortConfig.key === "status" ? (
                sortConfig.direction === "asc" ? (
                  <StraightIcon
                    style={{ transform: "rotate(-180deg)", fontSize: 16 }}
                  />
                ) : (
                  <StraightIcon style={{ fontSize: 16 }} />
                )
              ) : (
                <>
                  <StraightIcon
                    style={{
                      transform: "rotate(-180deg)",
                      fontSize: 16,
                      opacity: 0.5,
                    }}
                  />
                  <StraightIcon
                    style={{
                      fontSize: 16,
                      opacity: 0.5,
                    }}
                  />
                </>
              )} */}
            </div>
            <div style={{ width: "10%", fontWeight: 500 }}>Action</div>
          </div>

          <div className={`scroll_bar_y ${Styles.tableContent}`}>
            {isLoading ? (
              Array.from({ length: ROWS_TO_SHOW }).map((_, idx) => (
                <div
                  key={idx}
                  className={`display_flex_row ${Styles.tableDetails}`}
                  style={{ minHeight: 40, alignItems: "center" }}
                >
                  <div style={{ width: "5%" }}>
                    <Skeleton variant="text" width={20} />
                  </div>
                  <div style={{ width: "15%" }}>
                    <Skeleton variant="text" width={100} />
                  </div>
                  <div style={{ width: "10%" }}>
                    <Skeleton variant="text" width={70} />
                  </div>
                  <div style={{ width: "15%" }}>
                    <Skeleton variant="text" width={70} />
                  </div>
                  <div style={{ width: "15%" }}>
                    <Skeleton variant="text" width={90} />
                  </div>
                  <div style={{ width: "10%" }}>
                    <Skeleton variant="rectangular" width={60} height={24} />
                  </div>
                  <div style={{ width: "10%" }}>
                    <Skeleton variant="circular" width={24} height={24} />
                    <Skeleton
                      variant="circular"
                      width={24}
                      height={24}
                      style={{ marginLeft: 8 }}
                    />
                  </div>
                </div>
              ))
            ) : data.length === 0 ? (
              <div style={{ position: "relative", height: "100%" }}>
                <EmptyState />
              </div>
            ) : (
              sortedData.map((ele, index) => {
                const viewElem =
                  (user === "consumer" ? ele?.generator : ele?.consumer) || {};
                const isInterstate = viewElem?.isInterstate;
                const slots = viewElem?.slots || [];
                console.debug("slots", slots);
                const maxQty = slots.length
                  ? Math.max(
                      ...slots.map((s) => s.sellEnergy ?? s.purchaseEnergy ?? 0)
                    )
                  : "-";
                const minQty = slots.length
                  ? Math.min(
                      ...slots.map((s) => s.sellEnergy ?? s.purchaseEnergy ?? 0)
                    )
                  : "-";
                const maxRate = (() => {
                  // 1. Safety check: if no slots, return default
                  if (!slots || slots.length === 0) return "0";

                  const firstType = slots[0]?.energyRateType;

                  // 2. Check for DAM_PLUS or DAM_MINUS
                  if (firstType === "DAM_PLUS" || firstType === "DAM_MINUS") {
                    // Check if ANY slot is DAM_PLUS
                    const plusSlots = slots.filter(
                      (ele) => ele?.energyRateType === "DAM_PLUS"
                    );

                    if (plusSlots.length > 0) {
                      // SCENARIO: DAM_PLUS
                      // We want the HIGHEST addition (e.g. DAM+20 > DAM+10)
                      const maxPlus = Math.max(
                        ...plusSlots.map((s) =>
                          Number(s.energyRateStr?.split("+")?.[1] || 0)
                        )
                      );
                      return `DAM+${maxPlus}`;
                    } else {
                      // SCENARIO: DAM_MINUS
                      // We want the LOWEST subtraction to get the Max Rate (e.g. DAM-5 > DAM-10)
                      const minusSlots = slots.filter(
                        (ele) => ele?.energyRateType === "DAM_MINUS"
                      );
                      const minMinus = Math.min(
                        ...minusSlots.map((s) =>
                          Number(s.energyRateStr?.split("-")?.[1] || 0)
                        )
                      );
                      return `DAM-${minMinus}`;
                    }
                  }

                  // 3. Check for legacy/typo "DAM_"
                  if (firstType === "DAM_") {
                    const maxVal = Math.max(
                      ...slots.map((s) =>
                        Number(s.energyRateStr?.split("+")?.[1] || 0)
                      )
                    );
                    return `DAM+${maxVal}`;
                  }

                  // 4. Default fallback
                  return slots.length
                    ? Math.max(
                        ...slots.map((s) => s.sellingCost ?? s.landingCost ?? 0)
                      )
                    : "-";
                })();
                
                const minRate = (() => {
                  // 1. Safety check
                  if (!slots || slots.length === 0) return "0";

                  const firstType = slots[0]?.energyRateType;

                  // 2. Check for DAM_PLUS or DAM_MINUS
                  if (firstType === "DAM_PLUS" || firstType === "DAM_MINUS") {
                    // PRIORITY: Check for DAM_MINUS first (subtraction is always lower than addition)
                    const minusSlots = slots.filter(
                      (ele) => ele?.energyRateType === "DAM_MINUS"
                    );

                    if (minusSlots.length > 0) {
                      // SCENARIO: DAM_MINUS
                      // We want the LOWEST total rate.
                      // To get the lowest rate, we need the LARGEST subtraction (e.g. DAM-50 < DAM-10)
                      const maxSubtraction = Math.max(
                        ...minusSlots.map((s) =>
                          Number(s.energyRateStr?.split("-")?.[1] || 0)
                        )
                      );
                      return `DAM-${maxSubtraction}`;
                    } else {
                      // SCENARIO: DAM_PLUS (No minus slots exist)
                      // We want the LOWEST total rate.
                      // We need the SMALLEST addition (e.g. DAM+10 < DAM+50)
                      const plusSlots = slots.filter(
                        (ele) => ele?.energyRateType === "DAM_PLUS"
                      );

                      const minAddition = Math.min(
                        ...plusSlots.map((s) =>
                          Number(s.energyRateStr?.split("+")?.[1] || 0)
                        )
                      );
                      return `DAM+${minAddition}`;
                    }
                  }

                  // 3. Check for legacy/typo "DAM_"
                  if (firstType === "DAM_") {
                    // We want the smallest addition
                    const minVal = Math.min(
                      ...slots.map((s) =>
                        Number(s.energyRateStr?.split("+")?.[1] || 0)
                      )
                    );
                    return `DAM+${minVal}`;
                  }

                  // 4. Default fallback (Standard cost)
                  return slots.length
                    ? Math.min(
                        ...slots.map((s) => s.sellingCost ?? s.landingCost ?? 0)
                      )
                    : "-";
                })();

                return (
                  <div
                    key={ele?.id ?? `${viewElem?.name ?? "row"}-${index}`}
                    className={`display_flex_row ${Styles.tableDetails}`}
                  >
                    <div style={{ width: "5%" }}>{index + 1}</div>
                    <div style={{ width: "15%" }}>
                      {[
                        "active",
                        "received",
                        "placed",
                        "auto_rejected",
                        "rejected",
                      ].includes(String(ele?.status).toLowerCase()) ? (
                        <>{viewElem?.name}</>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            textAlign: "center",
                            gap: "5px",
                          }}
                        >
                          <div>
                            {viewElem?.generatorName || viewElem?.consumerName}
                          </div>
                          <div>{viewElem?.name}</div>
                        </div>
                      )}
                    </div>
                    <div style={{ width: "10%" }}>
                      {isInterstate === true
                        ? "Interstate"
                        : isInterstate === false
                        ? "Intrastate"
                        : "-"}
                    </div>
                    <div style={{ width: "15%" }}>
                      {maxQty} {maxQty !== "-" ? "(MW)" : ""}
                    </div>
                    <div style={{ width: "15%" }}>
                      {minQty} {minQty !== "-" ? "(MW)" : ""}
                    </div>
                    <div style={{ width: "15%" }}>
                      {console.debug("view", viewElem?.[0])}
                      {maxRate} {maxRate !== "-" ? "(₹/kWh)" : ""}
                    </div>
                    <div style={{ width: "15%" }}>
                      {minRate} {minRate !== "-" ? "(₹/kWh)" : ""}
                    </div>
                    <div style={{ width: "10%" }}>
                      <StatusPill status={ele?.status} />
                    </div>
                    <div
                      style={{ width: "10%" }}
                      className={Styles.actionIcons}
                    >
                      {["auto_rejected", "rejected"].includes(
                        String(ele?.status).toLowerCase()
                      ) ? (
                        <Chat style={{ cursor: "not-allowed" }} />
                      ) : (
                        <ChatActive
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            openBilateralChat(dispatch, {
                              header: viewElem?.name,
                              fromUser: activeMain?.getSelfListingById?.name,
                              toUser: viewElem?.name,
                            })
                          }
                        />
                      )}

                      <RemoveRedEyeIcon
                        style={{
                          cursor: "pointer",
                          color: "var(--primary-color-text)",
                        }}
                        onClick={() => handleOpenConnection(ele)}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <p className={Styles.noteText} style={{ marginTop: 10 }}>
            **Note: Accepted requests are automatically moved to the Active
            Connections tab.
          </p>
        </div>
      </div>
    </>
  );
};

export default Connection;
